import { milliseconds } from 'date-fns';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';

import V2Error from '@/app/V2Error';
import api from '@/services/api/v2';
import { ScheduleType, TimeType, operatingHoursKey } from '@/services/queryKey';
import { WeekDayEnum } from '@/types/enums';
import { ListQueryParamEnum } from '@/types/enums';

export interface OperatingHoursListQueryParams
  extends Partial<Record<ListQueryParamEnum, string | number>> {
  venueId: number;
  needAll?: 0 | 1;
  // name?: string; // TODO: 키워드 검색시 q(search)로 파라미터 통일 필요 (-> 영빈님한테 변경 요청 중) -> 변경 완료
}

/**
 * TO ID 목록
 */
type OperatingHoursIds = number[];

interface UpdateSubstituteCrewPayload extends SubstituteCrew.Payload {
  substituteId: number;
}

interface CreateOperatingHoursPayload {
  /**
   * 지점 ID
   */
  venue: number;
  operatingHourItem: {
    /**
     * TO 명
     */
    name: string;
    /**
     * TO 스케줄
     */
    operatingHourTimeItem: {
      /**
       * 요일
       */
      dayOfWeek: WeekDayEnum;

      /**
       * 시작 시간
       * @example 18:00
       */
      begin: string;
      /**
       * 종료 시간
       * @example 18:00
       */
      end: string;
      /**
       * 다음날 종료 여부
       * @example 18:00
       */
      willEndTomorrow?: boolean;
    }[];
  };
}

export const operatingHoursService = {
  /**
   * 지점 ID에 해당하는 전체 TO 리스트를 요청합니다.
   */
  async fetchOperatingHoursList({
    venueId,
    search,
    ...rest
  }: OperatingHoursListQueryParams) {
    try {
      const { data } = await api.get<OperatingHours.List>(
        `/operating-hours/web`,
        {
          params: {
            ...rest,
            venue: venueId,
            q: search,
          },
        }
      );

      return data;
    } catch (error) {
      throw new V2Error(error);
    }
  },
  /**
   * 지점 ID에 해당하는 전체 TO 리스트를 요청합니다.
   * @description Suspense 처리 X
   * @example TO 배정 모달 -> 커스텀 에러 핸들링
   */
  async fetchOperatingHoursListWithoutSuspense({
    venueId,
    search,
    ...rest
  }: OperatingHoursListQueryParams) {
    const { data } = await api.get<OperatingHours.List>(
      `/operating-hours/web`,
      {
        params: {
          ...rest,
          venue: venueId,
          q: search,
        },
      }
    );

    return data;
  },

  /**
   * 지점 ID에 해당하는 무한 스크롤 방식의 TO 리스트를 요청합니다.
   * @info 대시보드 > TO 검색 Filter에서 에러시 모달을 닫을 수 있도록 커스텀 에러 핸들링이 필요.
   */
  async fetchOperatingHoursListByScroll({
    venueId,
    search,
    ...rest
  }: OperatingHoursListQueryParams) {
    const { data } = await api.get<ListResponse<OperatingHours.Status[]>>(
      `/operating-hours/web/scroll`,
      {
        params: {
          ...rest,
          venue: venueId,
          q: search,
        },
      }
    );

    return data;
  },
  /**
   * 작업 스케줄을 포함한 TO 상세 데이터를 요청합니다.
   */
  async fetchOperatingHoursDetail(
    operatingHoursId: number,
    scheduleType: ScheduleType,
    timeType: TimeType
  ) {
    const { data } = await api.get<OperatingHours.Detail>(
      `/operating-hours/${operatingHoursId}/work-schedules/${scheduleType}`,
      {
        params: { timeType },
      }
    );

    return data;
  },
  /**
   * TO 간략 정보를 요청합니다.
   */
  async fetchOperatingHoursBrief(operatingHoursId: number) {
    const { data } = await api.get<OperatingHours.Brief>(
      `/operating-hours/${operatingHoursId}/short`
    );

    return data;
  },
  async fetchOperatingHoursBriefWithoutSuspense(operatingHoursId: number) {
    const { data } = await api.get<OperatingHours.Brief>(
      `/operating-hours/${operatingHoursId}/short`
    );

    return data;
  },
  /**
   * 새 TO 등록을 요청합니다.
   */
  async createOperatingHours(payload: CreateOperatingHoursPayload) {
    const { data } = await api.post<OperatingHoursIds>(
      `/operating-hours`,
      payload
    );
    return data;
  },
  /**
   * 기존 TO를 복제 요청합니다.
   */
  async copyOperatingHours(operatingHoursId: number) {
    const { data } = await api.post(
      `/operating-hours/${operatingHoursId}/clone`
    );
    return data;
  },
  /**
   * 기존 TO 이름 수정을 요청합니다.
   */
  async updateOperatingHoursName(operatingId: number, name: string) {
    const { data } = await api.patch<null>(
      `/operating-hours/${operatingId}/name`,
      {
        name,
      }
    );
    return data;
  },
  /**
   * 기존 TO를 삭제 요청합니다.
   */
  async deleteOperatingHours(operatingHoursId: number) {
    const { data } = await api.delete<null>(
      `/operating-hours/${operatingHoursId}`
    );
    return data;
  },
  /**
   * 대상 TO에 해당하는 대체 크루 (전체) 리스트 요청 쿼리키
   */
  async fetchSubstituteCrewList(operatingHoursId: number, targetDate: string) {
    const { data } = await api.get<SubstituteCrew.List>(
      `/operating-hours/${operatingHoursId}/substitute?targetDate=${targetDate}`
    );
    return data;
  },
  /**
   * 대상 TO에 크루 ID에 해당하는 크루를 메인 크루로 배정합니다.
   */
  async updateMainCrew(operatingHoursId: number, crewId: number) {
    const { data } = await api.patch<null>(
      `operating-hours/${operatingHoursId}/person-in-charge`,
      {
        user: crewId,
      }
    );
    return data;
  },
  /**
   * 대상 TO에 새 대체 크루를 배정합니다.
   */
  async createSubstituteCrew(payload: SubstituteCrew.Payload) {
    const { operatingId, ...rest } = payload;
    const { data } = await api.post<null>(
      `/operating-hours/${operatingId}/substitute`,
      rest
    );
    return data;
  },
  /**
   * 대상 TO에 기존 대체 크루를 새 대체 크루로 교체합니다.
   */
  async updateSubstituteCrew(payload: UpdateSubstituteCrewPayload) {
    const { operatingId, substituteId, ...rest } = payload;
    const { data } = await api.put<null>(
      `/operating-hours/${operatingId}/substitute/${substituteId}`,
      rest
    );

    return data;
  },
  /**
   * 대상 TO에 기존 대체 크루의 배정을 취소합니다.
   */
  async deleteSubstituteCrew(substituteId: number) {
    const { data } = await api.delete<null>(
      `/operating-hours/substitute/${substituteId}`
    );
    return data;
  },
};

// useFetchOperationSchedule -> useFetchOperatingHoursDetail
export function useFetchOperatingHoursDetail(
  operatingHoursId: number,
  scheduleType: ScheduleType = 'weekly',
  timeType: TimeType = 'start'
) {
  return useQuery<OperatingHours.Detail, V2Error>(
    operatingHoursKey.detailById(operatingHoursId, scheduleType, timeType),
    () =>
      operatingHoursService.fetchOperatingHoursDetail(
        operatingHoursId,
        scheduleType,
        timeType
      ),
    {
      enabled: Boolean(operatingHoursId),
      keepPreviousData: true,
      staleTime: milliseconds({ seconds: 30 }),
      suspense: false,
      useErrorBoundary: false,
    }
  );
}

export function useFetchOperatingHoursList(
  params: OperatingHoursListQueryParams
) {
  if (!params.venueId) {
    throw new ReferenceError(`'venueId' is undefined.`);
  }

  return useQuery<OperatingHours.List, V2Error>(
    operatingHoursKey.listAll({ ...params }),
    () => operatingHoursService.fetchOperatingHoursList(params),
    {
      staleTime: milliseconds({ minutes: 1 }),
    }
  );
}

export function useFetchOperatingHoursListWithoutSuspense(
  params: OperatingHoursListQueryParams
) {
  if (!params.venueId) {
    throw new ReferenceError(`'venueId' is undefined.`);
  }

  return useQuery<OperatingHours.List, V2Error>(
    operatingHoursKey.listAll({ ...params }),
    () => operatingHoursService.fetchOperatingHoursListWithoutSuspense(params),
    {
      staleTime: milliseconds({ minutes: 1 }),
      suspense: false,
      useErrorBoundary: false,
    }
  );
}

export function useFetchOperatingHoursListByScroll(
  params: OperatingHoursListQueryParams
) {
  return useInfiniteQuery(
    operatingHoursKey.listByScroll({ ...params }),
    ({ pageParam }) =>
      operatingHoursService.fetchOperatingHoursListByScroll({
        ...params,
        page: pageParam,
      }),
    {
      staleTime: milliseconds({ minutes: 1 }),
      suspense: false,
      useErrorBoundary: false,
      getNextPageParam(curr) {
        return curr.last ? undefined : curr.number + 1;
      },
    }
  );
}

export function useFetchOperatingHoursBrief(operatingHoursId: number) {
  return useQuery<OperatingHours.Brief, V2Error>(
    operatingHoursKey.briefById(operatingHoursId),
    () => operatingHoursService.fetchOperatingHoursBrief(operatingHoursId),
    {
      enabled: Number.isInteger(operatingHoursId) && operatingHoursId >= 0, // DEPENDENT QUERY
      retry: false,
      staleTime: 0,
      refetchOnWindowFocus: false,
      suspense: false,
      useErrorBoundary: false,
    }
  );
}

export function useFetchOperatingHoursBriefWithoutSuspense(
  operatingHoursId: number
) {
  return useQuery<OperatingHours.Brief, V2Error>(
    operatingHoursKey.briefById(operatingHoursId),
    () => operatingHoursService.fetchOperatingHoursBrief(operatingHoursId),
    {
      enabled: Number.isInteger(operatingHoursId) && operatingHoursId >= 0, // DEPENDENT QUERY
      retry: false,
      staleTime: 0,
      refetchOnWindowFocus: false,
      suspense: false,
      useErrorBoundary: false,
    }
  );
}

export function useAsyncCreateOperatingHours(
  onSuccess: (data: OperatingHoursIds) => void,
  onError: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<
    OperatingHoursIds,
    V2ErrorType,
    CreateOperatingHoursPayload
  >((payload) => operatingHoursService.createOperatingHours(payload), {
    onSuccess(data) {
      onSuccess && onSuccess(data);
    },
    onError(err) {
      onError && onError(err);
    },
  });

  return mutate;
}

export function useAsyncCopyOperatingHours(
  onSuccess: (data: null) => void,
  onError: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<null, V2ErrorType, number>(
    (operatingHoursId) =>
      operatingHoursService.copyOperatingHours(operatingHoursId),
    {
      onSuccess: (data) => {
        onSuccess && onSuccess(data);
      },
      onError: (error) => {
        onError && onError(error);
      },
    }
  );

  return mutate;
}

export function useAsyncUpdateOperatingHoursName(
  onSuccess: (data: null) => void,
  onError: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<
    null,
    V2ErrorType,
    { operatingId: number; name: string }
  >(
    ({ operatingId, name }) =>
      operatingHoursService.updateOperatingHoursName(operatingId, name),
    {
      onSuccess(data) {
        onSuccess && onSuccess(data);
      },
      onError(err) {
        onError && onError(err);
      },
    }
  );
  return mutate;
}

export function useAsyncDeleteOperatingHours(
  onSuccess: (data: null) => void,
  onError: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<null, V2ErrorType, number>(
    (operatingHoursId) =>
      operatingHoursService.deleteOperatingHours(operatingHoursId),

    {
      onSuccess(data) {
        onSuccess && onSuccess(data);
      },
      onError(err) {
        onError && onError(err);
      },
    }
  );
  return mutate;
}

export function useAsyncUpdateMainCrew(
  onSuccess: (data: null) => void,
  onError: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<
    null,
    V2ErrorType,
    {
      operatingHoursId: number;
      crewId: number;
    }
  >(
    ({ operatingHoursId, crewId }) =>
      operatingHoursService.updateMainCrew(operatingHoursId, crewId),
    {
      onSuccess(data) {
        onSuccess && onSuccess(data);
      },
      onError(err) {
        onError && onError(err);
      },
    }
  );

  return mutate;
}

export function useFetchSubstituteCrewList(
  operatingHoursId: number,
  targetDate: string
) {
  return useQuery(
    operatingHoursKey.substituteListById(operatingHoursId, targetDate),
    () =>
      operatingHoursService.fetchSubstituteCrewList(
        operatingHoursId,
        targetDate
      ),
    {
      enabled:
        Number.isInteger(operatingHoursId) &&
        operatingHoursId > 0 &&
        !!targetDate,
      staleTime: 0,
      suspense: false,
      useErrorBoundary: false,
    }
  );
}

export function useAsyncCreateSubstituteCrew(
  onSuccess: (data: null) => void,
  onError: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<null, V2ErrorType, SubstituteCrew.Payload>(
    (payload) => operatingHoursService.createSubstituteCrew(payload),
    {
      onSuccess(data) {
        onSuccess && onSuccess(data);
      },
      onError(err) {
        onError && onError(err);
      },
    }
  );

  return mutate;
}

export function useAsyncUpdateSubstituteCrew(
  onSuccess: (data: null) => void,
  onError: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<
    null,
    V2ErrorType,
    UpdateSubstituteCrewPayload
  >((payload) => operatingHoursService.updateSubstituteCrew(payload), {
    onSuccess(data) {
      onSuccess && onSuccess(data);
    },
    onError(err) {
      onError && onError(err);
    },
  });

  return mutate;
}

export function useAsyncDeleteSubstituteCrew(
  onSuccess: (data: null) => void,
  onError: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<null, V2ErrorType, { substituteId: number }>(
    ({ substituteId }) =>
      operatingHoursService.deleteSubstituteCrew(substituteId),
    {
      onSuccess(data) {
        onSuccess && onSuccess(data);
      },
      onError(err) {
        onError && onError(err);
      },
    }
  );

  return mutate;
}
